<template>
  <modal :show="showModal" modal-classes="modal-secondary" size="lg">
    <div class="row">
      <div class="col-12">
        <base-input :label="`${$t('COMMON.DESTINATION')} *`">
          <establishment-selector :onlyActive="true"
            :allowNone="false"
            :establishment="destinationEstablishmentId"
            :filterOrganization="establishment.organization?.id"
            :bypassScope="true"
            @establishmentChanged="onDestinationChanged"
          />
        </base-input>
        <validation-error :errors="apiValidationErrors.destinationEstablishment" />
      </div>

      <div class="col-12">
        <base-input :label="`${$t('ESTABLISHMENT_TRANSFERS.TRANSFER_DATE')} *`">
          <flat-picker
            v-model="transferDate"
            :config="{
              allowInput: true,
              locale: $flatPickrLocale(),
            }"
            class="form-control datepicker"
          />
        </base-input>
        <validation-error :errors="apiValidationErrors.transfer_date" />
      </div>

      <div class="col-12">
        <base-input :label="$t('COMMON.NOTE')" :placeholder="$t('COMMON.NOTE')">
          <textarea class="form-control" rows="3" v-model="excerpt"></textarea>
        </base-input>
        <validation-error :errors="apiValidationErrors.excerpt" />
      </div>
    </div>

    <template slot="footer">
      <button
        type="submit"
        class="btn btn-primary"
        @click="createTransfer"
        :disabled="loading || !destinationEstablishmentId"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("COMMON.SAVE") }}
      </button>

      <button
        type="button"
        class="btn btn-link ml-auto"
        @click="closeModal"
        :disabled="loading"
      >
        {{ $t("COMMON.CLOSE") }}
      </button>
    </template>
  </modal>
</template>

<script>
import { cloneDeep } from "lodash";
import moment from "moment";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import formMixin from "@/mixins/form-mixin";
import ValidationError from "@/components/ValidationError.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";
import defaultTransfer from "../../../InventoryModule/EstablishmentTransferManagement/defaultEstablishmentTransfer";

export default {
  name: "EstablishmentViewTransfersAddTransferModal",
  components: {
    EstablishmentSelector,
    ValidationError,
    flatPicker,
  },
  mixins: [formMixin],
  props: {
    establishment: {
      type: Object,
      required: true,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      destinationEstablishmentId: null,
      transferDate: moment().format("YYYY-MM-DD"),
      excerpt: "",
    };
  },
  watch: {
    showModal(value) {
      if (value) {
        // Recalcule la date du jour à chaque ouverture (évite une date figée
        // au chargement de la page si l'onglet reste ouvert plusieurs jours).
        this.transferDate = moment().format("YYYY-MM-DD");
      } else {
        this.resetForm();
      }
    },
    establishment: {
      deep: true,
      handler() {
        this.resetForm();
      },
    },
  },
  methods: {
    onDestinationChanged(establishmentId) {
      this.destinationEstablishmentId = establishmentId;
      this.resetApiValidation();
    },
    resetForm() {
      this.loading = false;
      this.destinationEstablishmentId = null;
      this.transferDate = moment().format("YYYY-MM-DD");
      this.excerpt = "";
      this.resetApiValidation();
    },
    closeModal() {
      this.resetForm();
      this.$emit("onCloseModal");
    },
    async createTransfer() {
      if (!this.establishment?.id || !this.destinationEstablishmentId) {
        return;
      }
      this.loading = true;
      const payload = cloneDeep(defaultTransfer);
      payload.transfer_date = this.transferDate;
      payload.excerpt = this.excerpt;
      payload.organization = {
        type: "organizations",
        id: this.establishment.organization?.id,
      };
      payload.sourceEstablishment = {
        type: "establishments",
        id: this.establishment.id,
      };
      payload.destinationEstablishment = {
        type: "establishments",
        id: this.destinationEstablishmentId,
      };
      if (this.establishment.serviceCenter?.id) {
        payload.serviceCenter = {
          type: "service-centers",
          id: this.establishment.serviceCenter.id,
        };
      }

      try {
        await this.$store.dispatch("establishmentTransfers/add", {
          data: payload,
        });
        const transfer = this.$store.getters["establishmentTransfers/transfer"];
        this.$notify({
          type: "success",
          message: this.$t("COMMON.SAVED"),
        });
        this.$emit("created", transfer);
        this.closeModal();
        this.$router.push({
          name: "View EstablishmentTransfer",
          params: { id: transfer.id },
        });
      } catch (error) {
        if (error?.response?.data?.errors) {
          this.setApiValidation(error.response.data.errors);
        }
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
